/**
 * Progress persistence.
 *
 * Answers + current step are written to sessionStorage so a refresh or an
 * accidental back-navigation does not lose the funnel. PLAN.md §6.3.
 * sessionStorage (not localStorage) — progress is scoped to the browser tab
 * and clears when the funnel is genuinely abandoned.
 */
import type { Answers } from "../types";

export interface PersistedProgress {
  session_id: string;
  step_id: string;
  answers: Answers;
  /**
   * Engine back-navigation stack — the sequence of step ids the user advanced
   * through to reach `step_id`. Persisted so a refresh / popstate after restore
   * still has a populated history to walk backward through.
   */
  history?: string[];
  updated_at: number;
}

const key = (funnelId: string): string => `funnel:${funnelId}:progress`;

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined; // private-mode / sandboxed contexts
  }
}

export function loadProgress(funnelId: string): PersistedProgress | undefined {
  const raw = storage()?.getItem(key(funnelId));
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as PersistedProgress;
    if (parsed && parsed.session_id && parsed.step_id) return parsed;
  } catch {
    // corrupt entry — ignore and start fresh
  }
  return undefined;
}

export function saveProgress(funnelId: string, p: PersistedProgress): void {
  try {
    storage()?.setItem(key(funnelId), JSON.stringify({ ...p, updated_at: Date.now() }));
  } catch {
    // quota / disabled storage — progress persistence is best-effort
  }
}

export function clearProgress(funnelId: string): void {
  try {
    storage()?.removeItem(key(funnelId));
  } catch {
    /* ignore */
  }
}

/** A stable per-session id; reused across a refresh within the same tab. */
export function getOrCreateSessionId(funnelId: string): string {
  const existing = loadProgress(funnelId)?.session_id;
  if (existing) return existing;
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return rand;
}
